/**
 * Created: 2026-06-24
 * Initial commit: Refactor match building logic (feat/event/mr-17)
 *
 * Generic match builder for the event plan view - constructs Match objects from seeding numbers
 * and event match records. Supports singles, doubles, and Australian doubles.
 */

import { expandCompetitors } from './competitors'
import type { Event, PlayerUser, SeedingNumber } from './types'

export type Player = Omit<PlayerUser, 'id'> & {
  id: string
  seeding: SeedingNumber
  // The player's competitor's 1-based position among the event's competitors
  // (its seed number). Equal to `seeding` for one-player-per-competitor formats;
  // shared by both members of a fixed-partner Doubles team.
  competitorIndex: number
}

export interface MatchSide {
  players: Player[]
}

export interface SetResult {
  side1Score: number
  side2Score: number
  side1TieBreak?: number
  side2TieBreak?: number
}

export interface Match {
  round: number
  side1: MatchSide
  side2: MatchSide
  sets: SetResult[]
  matchId?: string
}

/**
 * Build a Match object from seeding numbers and event data.
 *
 * @param round - Round number
 * @param side1Seedings - Seeding numbers for side 1 (single player or team)
 * @param side2Seedings - Seeding numbers for side 2 (single player or team)
 * @param event - Event containing competitors and existing match records
 * @returns Match object for the plan view
 */
export function buildMatch(
  round: number,
  side1Seedings: number[],
  side2Seedings: number[],
  event: Pick<Event, 'competitors' | 'matches'>,
): Match {
  const expandedBySeeding = expandCompetitors(event.competitors)
  const playersBySeeding = expandedBySeeding.map((e) => e.player)

  // Extract player IDs for both sides
  // biome-ignore lint/style/noNonNullAssertion: Player.id is guaranteed non-null
  const side1PlayerIds = side1Seedings.map((s) => playersBySeeding[s - 1].id!)
  // biome-ignore lint/style/noNonNullAssertion: Player.id is guaranteed non-null
  const side2PlayerIds = side2Seedings.map((s) => playersBySeeding[s - 1].id!)

  // Find existing match record in event.matches
  // Match when round matches and all players on both sides are present (regardless of side assignment)
  const matchRecord = event.matches.find((m) => {
    if (m.round !== round) return false
    const matchPlayerIds = m.sides.flatMap((s) => s.players.map((p) => p.id))
    return (
      side1PlayerIds.every((id) => matchPlayerIds.includes(id)) &&
      side2PlayerIds.every((id) => matchPlayerIds.includes(id))
    )
  })

  // Determine which side is stored as side 1 vs side 2 in the backend
  // Check if any player from side1 appears in the backend's side[0]
  const side1IsSide1 = matchRecord?.sides[0].players.some(
    // biome-ignore lint/style/noNonNullAssertion: player id is always set
    (p) => side1PlayerIds.includes(p.id!),
  )

  // Map sets with correct orientation
  const sets: SetResult[] = (matchRecord?.sets ?? []).map((s) => ({
    side1Score: side1IsSide1 ? s.side1Score : s.side2Score,
    side2Score: side1IsSide1 ? s.side2Score : s.side1Score,
    side1TieBreak: side1IsSide1
      ? s.side1TiebreakPoints || undefined
      : s.side2TiebreakPoints || undefined,
    side2TieBreak: side1IsSide1
      ? s.side2TiebreakPoints || undefined
      : s.side1TiebreakPoints || undefined,
  }))

  return {
    round,
    matchId: matchRecord?.id,
    side1: {
      players: side1Seedings.map((seeding) => ({
        seeding,
        competitorIndex: expandedBySeeding[seeding - 1].competitorIndex,
        ...playersBySeeding[seeding - 1],
        // biome-ignore lint/style/noNonNullAssertion: Player.id is guaranteed non-null
        id: playersBySeeding[seeding - 1].id!,
      })),
    },
    side2: {
      players: side2Seedings.map((seeding) => ({
        seeding,
        competitorIndex: expandedBySeeding[seeding - 1].competitorIndex,
        ...playersBySeeding[seeding - 1],
        // biome-ignore lint/style/noNonNullAssertion: Player.id is guaranteed non-null
        id: playersBySeeding[seeding - 1].id!,
      })),
    },
    sets,
  }
}
